// The instruction editor's operations, as pure functions over the steps: easy
// to test, and the editor stays a thin view over them.
import type { CheckRequest } from '../../api/generated/CheckRequest';
import type { InstructionSetResponse } from '../../api/generated/InstructionSetResponse';
import type { InstructionStepRequest } from '../../api/generated/InstructionStepRequest';

export type Steps = readonly InstructionStepRequest[];

/** The set's steps as an edit starts from them. */
export function draftOf(set: InstructionSetResponse): InstructionStepRequest[] {
    return set.steps.map((step) => ({
        keys: [...step.keys],
        title: step.title,
        note: step.note,
        cautions: [...step.cautions],
        photos: [...step.photos],
        checks: step.checks.map((check) => ({
            checkId: check.checkId,
            name: check.name,
            unit: check.unit,
            min: check.min,
            max: check.max,
        })),
    }));
}

export function moveStep(steps: Steps, index: number, by: -1 | 1): InstructionStepRequest[] {
    const target = index + by;
    if (target < 0 || target >= steps.length) return [...steps];
    const next = [...steps];
    const moved = next[index];
    const other = next[target];
    if (!moved || !other) return next;
    next[index] = other;
    next[target] = moved;
    return next;
}

/** Merges a step with the one after it: their generated steps, cautions, photos and checks together. */
export function mergeWithNext(steps: Steps, index: number): InstructionStepRequest[] {
    const first = steps[index];
    const second = steps[index + 1];
    if (!first || !second) return [...steps];
    const merged: InstructionStepRequest = {
        keys: [...first.keys, ...second.keys],
        title: first.title,
        note: [first.note, second.note].filter(Boolean).join('\n') || null,
        cautions: [...first.cautions, ...second.cautions],
        photos: [...first.photos, ...second.photos],
        checks: [...first.checks, ...second.checks],
    };
    return [...steps.slice(0, index), merged, ...steps.slice(index + 2)];
}

/** Splits a merged step back into one step per generated step; its additions stay on the first. */
export function split(steps: Steps, index: number): InstructionStepRequest[] {
    const step = steps[index];
    if (!step || step.keys.length < 2) return [...steps];
    const [first, ...rest] = step.keys;
    const parts: InstructionStepRequest[] = [
        { ...step, keys: first === undefined ? [] : [first] },
        ...rest.map((key) => ({ keys: [key], title: null, note: null, cautions: [], photos: [], checks: [] })),
    ];
    return [...steps.slice(0, index), ...parts, ...steps.slice(index + 1)];
}

export function updateStep(
    steps: Steps,
    index: number,
    change: Partial<InstructionStepRequest>,
): InstructionStepRequest[] {
    return steps.map((step, position) => (position === index ? { ...step, ...change } : step));
}

export const newCheck = (): CheckRequest => ({ checkId: null, name: '', unit: null, min: null, max: null });
